import React, { useState, useReducer, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { subscribe } from 'alfa';
import { Button, Typography } from 'antd';
import { withTranslation } from 'react-i18next';

import TransactionFilter from '../../filters/transaction';
import { TransactionRepo } from '../../repository/transaction';
import AccountTypeRepo from '../../repository/account-type';
import NewTransaction from './components/new';
import ExportList from './components/ExportList';
import { IndexList } from './components/IndexList';
import { TransactionContext, transactionReducer } from './transactionContext';

const { Title } = Typography;

const fetchLimit = 80;

const buildFilter = ({ account, limit, filters: { dateRange } }) => new TransactionFilter(
  account,
  limit,
  {
    date: (dateRange) ? { start: dateRange[0], end: dateRange[1] } : { start: null, end: null },
  },
);

const TransactionIndex = ({ business, account, t }) => {
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transactionsState, dispatchTransactions] = useReducer(transactionReducer, []);
  const actions = AccountTypeRepo.transactionActions(account.product_id);
  const showBankAccount = actions.includes('spei');

  const clearList = useCallback(() => {
    dispatchTransactions({ type: 'reset' });
  }, []);

  const getNextPage = useCallback(async () => {
    clearList();
    setLoading(true);
    const options = { account, limit: fetchLimit, filters: { dateRange: filterDateRange } };
    const filter = buildFilter(options);

    const { data: { transfers = [] } = {} } = await TransactionRepo.fetchCollection(filter);

    dispatchTransactions({ type: 'push', payload: transfers });
    setLoading(false);
    return transfers;
  }, [account, filterDateRange, clearList]);

  const getTransactions = async ({ limit = null } = {}) => {
    const options = { account, limit, filters: { dateRange: filterDateRange } };
    const filter = buildFilter(options);

    const { data: { transfers = [] } = {} } = await TransactionRepo.fetchCollection(filter);
    return transfers;
  };

  const changeFilters = ({ dateRange }) => {
    setFilterDateRange(dateRange);
  };

  useEffect(() => {
    setCount(transactionsState.length);
  }, [transactionsState]);

  useEffect(() => {
    getNextPage();
  }, [getNextPage]);

  return (
    <TransactionContext.Provider value={{ transactions: transactionsState, dispatchTransactions }}>
      <Title level={3}>
        {t('transactions')}
        <Button.Group style={{ float: 'right' }}>
          <NewTransaction business={business} account={account} />
          <ExportList getData={getTransactions} disabled={!count} />
        </Button.Group>
      </Title>

      <IndexList
        getNextPage={getNextPage}
        setFilters={changeFilters}
        showBankAccount={showBankAccount}
        loading={loading}
      />
    </TransactionContext.Provider>
  );
};

TransactionIndex.propTypes = {
  account: PropTypes.object,
  business: PropTypes.object,
  t: PropTypes.func,
};

export { TransactionIndex };
export default subscribe(withTranslation()(TransactionIndex), ['business', 'account']);
